import { bool, object } from 'prop-types';
import React from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import { defineMessages, intlShape, injectIntl } from 'react-intl';
import { trackEvent } from 'dibs-tracking';
import { localStorage } from 'dibs-browser-storage';
import SearchBarOverlay from './SearchBarOverlay';
import SearchButton from './SearchButton';
import ClearButton from './ClearButton';
import { RECENT_SEARCHES, SEARCH_PAGE_PATH, SEARCH_QUERY_PARAM, TRACKING } from './searchBarConstants';
import debounce from '../../utils/debounce';
import qs from 'querystring';
import classnames from 'classnames';

import styles from './SearchBar-style.scss';

const messages = defineMessages({
    placeholder: {
        id: 'dbl.Header.SearchBar.placeholder',
        defaultMessage: 'Search for items, creators, or styles'
    }
});

export class SearchBar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: '',
            focused: false,
            hovered: false
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleClearSearch = this.handleClearSearch.bind(this);
        this.fetchSuggestions = debounce(this.fetchSuggestions.bind(this), 250);

        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);
    }

    componentDidMount() {
        this.input.addEventListener('focus', this.onFocus);
        this.input.addEventListener('blur', this.onBlur);
        this.overlayWrapper.addEventListener('mouseover', this.onMouseOver);
        this.overlayWrapper.addEventListener('mouseout', this.onMouseOut);
        this.setSearchTermFromParams();
    }

    componentDidUpdate(prevProps) {
        if (this.props.viewer.searchSuggestions && !prevProps.viewer.searchSuggestions) {
            trackEvent({
                category: TRACKING.CATEGORY,
                action: 'search suggestion displayed',
                label: 'none'
            });
        }
    }

    componentWillUnmount() {
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('blur', this.onBlur);
        this.overlayWrapper.removeEventListener('mouseover', this.onMouseOver);
        this.overlayWrapper.removeEventListener('mouseout', this.onMouseOut);
    }

    setSearchTermFromParams() {
        const queryString = location.search ? location.search.split('?')[1] : '';
        const searchTermFromParams = decodeURIComponent(qs.parse(queryString)[SEARCH_QUERY_PARAM]);
        if (location.href.indexOf(SEARCH_PAGE_PATH) !== -1 && searchTermFromParams) {
            this.setState({ value: searchTermFromParams });
        }
    }

    onFocus() {
        this.setState({focused: true});
    }

    onBlur() {
        this.setState({focused: false});
    }

    onMouseOver() {
        this.setState({hovered: true});
    }

    onMouseOut() {
        this.setState({hovered: false});
    }

    handleRowClick(value, href) {
        const label = this.props.viewer.searchSuggestions ? 'global search|auto-complete selected' : 'recent search';
        this.input.value = value;
        this.setState({ value, focused: false, hovered: false });

        trackEvent({
            category: TRACKING.CATEGORY,
            action: TRACKING.ACTION,
            label
        });

        location.href = href;
    }

    handleKeyDown(event) {
        if (event.keyCode === 27) {
            this.input.blur();
            this.setState({visible: false});
        }
    }

    handleChange(event) {
        const value = event.target.value || '';
        this.setState({ value }, this.fetchSuggestions);
    }

    handleClearSearch() {
        this.setState({ value: '' });
    }

    fetchSuggestions() {
        const { value } = this.state;
        const { hasRootPendingRefetch } = this.props;
        if (!hasRootPendingRefetch) {
            this.props.relay.refetch(() => ({
                hasQuery: value.length > 1,
                query: value.toLowerCase()

            }), null);
        }
    }

    handleSubmit(event) {
        event.preventDefault();
        const { value: term } = this.state;
        let urlLabel;

        if (term) {
            urlLabel = `${location.origin}/search/?q=${encodeURIComponent(term.toLowerCase())}`;
            trackEvent({
                category: TRACKING.CATEGORY,
                action: TRACKING.ACTION,
                label: 'global search'
            });
            this.handleRecentStorage(term, urlLabel);
            location.href = urlLabel;
        }
    }

    handleRecentStorage(term, urlLabel) {
        const search = {term, urlLabel};
        let recentSearches = localStorage.getItem(RECENT_SEARCHES.LOCAL_STORAGE) || [];
        let recentTerms = [];

        if (!recentSearches.length) {
            recentSearches = [{
                type: RECENT_SEARCHES.TYPE,
                terms: [search]
            }];
        } else {
            recentTerms = recentSearches[0].terms;
            if (recentTerms.length === RECENT_SEARCHES.MAX_TERMS) {
                recentTerms.pop();
            }
            recentTerms.unshift(search);
        }

        localStorage.setItem(RECENT_SEARCHES.LOCAL_STORAGE, JSON.stringify(recentSearches));
    }

    render() {
        const { intl, isClient, viewer } = this.props;
        const { value } = this.state;
        const { suggestions } = viewer.searchSuggestions || {};
        const recentSearches = localStorage.getItem(RECENT_SEARCHES.LOCAL_STORAGE) || [];
        let visible = this.state.focused || this.state.hovered;

        const inputClasses = classnames(styles.input, {
            [styles.hasTerm]: !!value
        });

        return (
            <div className={styles.wrapper}>
                <form className={styles.form} action="/search/" onSubmit={this.handleSubmit}>
                    <input
                        ref={(input) => { this.input = input; }}
                        autoCorrect="off"
                        autoCapitalize="off"
                        autoComplete="off"
                        spellCheck="false"
                        className={inputClasses}
                        type='text'
                        name="q"
                        placeholder={intl.formatMessage(messages.placeholder)}
                        value={value}
                        onChange={this.handleChange}
                        onKeyDown={this.handleKeyDown}
                        data-tn='global-header-search-input'
                    />
                    {value && <ClearButton onClick={this.handleClearSearch} />}
                    <SearchButton hasSearchTerm={!!value} />
                </form>
                <div className={styles.overlayWrapper} ref={(overlayWrapper) => { this.overlayWrapper = overlayWrapper; }}>
                    {isClient ?
                        <SearchBarOverlay
                            handleRowClick={this.handleRowClick}
                            visible={visible}
                            suggestions={suggestions || recentSearches}
                        /> : null}
                </div>
            </div>
        );
    }
}

SearchBar.propTypes = {
    hasRootPendingRefetch: bool,
    isClient: bool,
    intl: intlShape,
    relay: object,
    viewer: object
};

export default createRefetchContainer(
    injectIntl(SearchBar),
    {
        viewer: graphql.experimental`
            fragment SearchBar_viewer on Viewer
            @argumentDefinitions(
                query: {type: "String!", defaultValue: ""}
                hasQuery: {type: "Boolean!", defaultValue: false}
            ) {
                searchSuggestions(query: $query, includeTypes: ["POPULAR", "SELLER"]) @include(if: $hasQuery) {
                    suggestions {
                        type,
                        terms {
                            term,
                            urlLabel
                        }
                    }
                }
            }
        `
    },
        graphql.experimental`
            query SearchBarRefetchQuery(
                $query: String!
                $hasQuery: Boolean!
            ) {
               viewer {
                    ...SearchBar_viewer @arguments(query: $query, hasQuery: $hasQuery)
                }
            }
    `
);



// WEBPACK FOOTER //
// ./src/components/SearchBar/SearchBar.jsx